import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Sparkles, Users, Heart } from 'lucide-react';

const WHATSAPP_URL = 'https://wa.me/message/AIKG7VFJUUNMJ1';

const IMAGES = {
  soundHealing: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/vkqrk8xy_singing%20bowls.png',
  bioenergy: '/images/energy-healing-hands.webp',
  reiki: 'https://customer-assets.emergentagent.com/job_5bf56e5f-42e0-4cfb-a653-edc31a7a571c/artifacts/jbr2lcfd_Miri%20Reiki.JPG',
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export const EnergyWork = () => {
  const { t, language } = useLanguage();
  const isSpanish = language === 'es';
  const bioenergy = {
    name: 'Bioenergy Healing Massage',
    desc: isSpanish
      ? 'Una experiencia de 90 minutos que comienza con 60 minutos de masaje personalizado y continúa con 30 minutos de trabajo bioenergético, acompañados por cuencos tibetanos.'
      : 'A 90-minute experience beginning with 60 minutes of personalized massage, followed by 30 minutes of bioenergy healing, accompanied by Tibetan singing bowls.',
    detail: isSpanish
      ? 'Disponible individualmente o en pareja. Después del masaje, recibes el trabajo energético con ropa puesta y movimientos suaves de las manos alrededor del cuerpo. En pareja, una terapeuta y un terapeuta complementan su práctica para acompañar la experiencia compartida.'
      : 'Available individually or for couples. After your massage, you receive the energy work fully clothed, with gentle hand movements around the body. For couples, a female and a male therapist bring complementary approaches to your shared experience.',
    tagline: isSpanish ? 'Un momento de calma. Una conexión más profunda.' : 'A moment of calm. A deeper connection.',
    article: isSpanish ? 'Conoce el trabajo energético: Reiki y bioenergía' : 'Explore energy work: Reiki & bioenergy healing',
  };

  const services = [
    {
      key: 'soundHealing',
      icon: Sparkles,
      image: IMAGES.soundHealing,
    },
    {
      key: 'bioenergy',
      icon: Heart,
      image: IMAGES.bioenergy,
    },
    {
      key: 'groupReiki',
      icon: Users,
      image: IMAGES.reiki,
    },
  ];

  return (
    <div data-testid="energy-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.soundHealing}
            alt="Energy Work"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#C9B99A] mb-4 font-['Manrope'] font-semibold">
              {t('energy.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white mb-6 tracking-tight">
              {t('energy.title')}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-white/80 mb-8 font-['Manrope']">
              {t('energy.description')}
            </motion.p>
            
            <motion.div variants={fadeInUp}>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#C9B99A] hover:bg-[#C49366] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                  {t('cta.contact')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 md:py-32 px-6" data-testid="energy-services">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-16"
          >
            {services.map((service, index) => (
              <motion.div
                key={service.key}
                id={service.key === 'bioenergy' ? 'bioenergy-healing-massage' : undefined}
                style={service.key === 'bioenergy' ? { scrollMarginTop: '7rem' } : undefined}
                variants={fadeInUp}
                className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <img
                    src={service.image}
                    alt={service.key === 'bioenergy' ? (isSpanish ? 'Imagen simbólica de manos con luz dorada en la selva' : 'Symbolic image of hands holding golden light in the jungle') : t(`energy.${service.key}.name`)}
                    className={`rounded-2xl shadow-xl w-full object-cover ${service.key === 'bioenergy' ? 'aspect-[2/3] max-w-md mx-auto' : 'aspect-[4/3]'}`}
                  />
                </div>
                
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="w-16 h-16 rounded-full bg-[#F8F6F3] flex items-center justify-center mb-6">
                    <service.icon className="w-8 h-8 text-[#A91B60]" />
                  </div>
                  <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-4 tracking-tight">
                    {service.key === 'bioenergy' ? bioenergy.name : t(`energy.${service.key}.name`)}
                  </h2>
                  <p className="text-lg text-[#5A5A5A] leading-relaxed mb-8 font-['Manrope']">
                    {service.key === 'bioenergy' ? bioenergy.desc : t(`energy.${service.key}.desc`)}
                  </p>
                  {service.key === 'bioenergy' && (
                    <div className="mb-8 space-y-5 font-['Manrope'] text-[#5A5A5A] leading-relaxed">
                      <p>{bioenergy.detail}</p>
                      <p className="font-['Playfair_Display'] italic text-xl text-[#75445F]">{bioenergy.tagline}</p>
                      <Link to="/articles/energy-work-reiki-bioenergy" className="inline-flex items-center gap-2 text-[#A91B60] font-semibold underline">{bioenergy.article}<ArrowRight size={18} /></Link>
                    </div>
                  )}
                  {service.key === 'groupReiki' && <p className="mb-8 font-['Manrope'] text-[#5A5A5A]">{isSpanish ? 'Experiencia grupal de Reiki sin masaje.' : 'A group Reiki experience without massage.'}</p>}
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                      {t('cta.contact')}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </a>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#F8F6F3]">
        <div className="max-w-3xl mx-auto text-center px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] mb-6 tracking-tight">
              {isSpanish ? 'Encuentra tu ' : 'Find your '}<em className="italic text-[#A91B60]">{isSpanish ? 'equilibrio' : 'balance'}</em>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#5A5A5A] mb-10 font-['Manrope']">
              {isSpanish ? 'Regálate una pausa con trabajo energético y cuencos tibetanos.' : 'Take time to pause with energy work and Tibetan singing bowls.'}
            </motion.p>
            <motion.div variants={fadeInUp}>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-10 py-6 font-['Manrope'] font-semibold">
                  {t('cta.contact')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EnergyWork;
